import React, { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import config from "../../../config";
import {
  handleAccept,
  handleClose,
  handlePause,
  handleReactivate,
  handleReopen,
} from "../../../utils/Change/changeHandlers";
import { ChangeData } from "../../../utils/Change/changeTypes";
import "../Modal.css";
import ConfirmCloseModal from "./ConfirmClose";
import ForwardModal from "./ForwardModal";
import JournalModal from "./JournalModal";
import CommunicationModal from "./CommunicationModal";
import ChangePhaseModel from "./ChangePhaseModel";
import { authFetch } from "../../../utils/Security/authFetch";

type Message = {
  id: number;
  username: string;
  message: string;
  date: string;
};
type Communication = {
  id: number;
  users: string;
  message: string;
  date: string;
};

type ModalProps = {
  isOpen: boolean;
  data: any;
  onClose: () => void;
};

const hiddenKeys = [
  "isaccepted",
  "isFavorite",
  "isallsite",
  "isclosed",
  "foruserid",
  "errorType",
  "isforwarded",
  "isreopened",
  "isreactivated",
];

// Sections
const ticketInfo = [
  "createdby",
  "userid",
  "ticketId",
  "status",
  "phase",
  "ticketCreationDatetime",
  "ticketClosureDatetime",

  "changeStartDatetime",
  "changeCloseDatetime",
  "imapctchangeStartDatetime",
  "impactchangeCloseDatetime",
  "changeResolutionTime",
  "customerapproval",
  "cabapproval",
  "closedby",
];
const customerInfo = [
  "affectedCustomer",
  "affectedNetwork",
  "affectedSite",
  "affectedLink",
  "asset",
  "affectedAntenna",
];

const statusInfo = [
  "summary",
  "description",
  "risk",
  "rollbackstrategy",
  "requiredresources",
  "solution",
  "closureCode",
];
const template = ["changetemplate", "typeofchange"];
const resInfo = ["responsiblePerson", "responsibleTeam"];
const miscInfo = ["journalUpdate", "changetemplate", "attachments", "filePath"];
const incInfo = ["priority"];

const relInfo = ["filePath"];

const fieldLabels: { [key: string]: string } = {
  // Ticket Info
  createdby: "Created By",
  userid: "User",
  ticketId: "Ticket ID",
  status: "Status",
  phase: "Phase",
  ticketCreationDatetime: "Ticket Creation Date/Time",
  ticketClosureDatetime: "Ticket Closure Date/Time",
  changeStartDatetime: "Planned Start Date/Time",
  changeCloseDatetime: "Planned End Date/Time",
  imapctchangeStartDatetime: "Impact Planned Start Date/Time",
  imapctchangeCloseDatetime: "Impact Planned End Date/Time",
  impactchangeCloseDatetime: "Impact Planned End Date/Time",
  changeResolutionTime: "Impact Period",
  customerapproval: "Customer Approval",
  cabapproval: "CAB Approval",
  typeofchange: "Type of Change",
  affectedCustomer: "Customer",
  affectedNetwork: "Network",
  affectedSite: "Site",
  affectedLink: "Link",
  asset: "Asset",
  affectedAntenna: "Antenna",

  // Status Info
  summary: "Summary",
  description: "Description",
  risk: "Risk",
  rollbackstrategy: "Rollback Strategy",
  requiredresources: "Required Resources",
  solution: "Solution",
  closureCode: "Closure Code",

  // Responsible Info
  responsiblePerson: "Responsible Person",
  responsibleTeam: "Responsible Team",

  // Misc Info
  journalUpdate: "Journal Update",
  changetemplate: "Change Template",
  attachments: "Attachments",
  filePath: "File Path",

  // Incident Info
  priority: "Priority",

  closedby: "Closed By",
};
const renderAttachment = (filePaths: string[] | undefined) => {
  if (!filePaths || filePaths.length === 0) return null;

  return (
    <>
      {filePaths.map((filePath, index) => {
        const extension = filePath.split(".").pop()?.toLowerCase();
        const url = `http://149.202.42.77:8080/${filePath.replace(/\\/g, "/")}`;

        if (!extension) return null;

        if (["png", "jpg", "jpeg", "gif", "bmp"].includes(extension)) {
          return (
            <img
              key={index}
              src={url}
              alt="attachment"
              style={{
                maxWidth: "100%",
                maxHeight: 300,
                display: "block",
                marginBottom: 10,
              }}
            />
          );
        }

        if (extension === "pdf") {
          return (
            <iframe
              key={index}
              src={url}
              title={`PDF-${index}`}
              style={{
                width: "100%",
                height: "500px",
                border: "none",
                marginBottom: 10,
              }}
            />
          );
        }

        return (
          <a
            key={index}
            href={url}
            target="_blank"
            rel="noopener noreferrer"
            download
            style={{ display: "block", marginBottom: 10 }}
          >
            Download attachment ({extension.toUpperCase()})
          </a>
        );
      })}
    </>
  );
};
const formatDate = (dateStr: string): string => {
  if (!dateStr) return "";
  return dateStr.replace("T", " ").split(".")[0];
};
const downloadFile = async (path: string) => {
  try {
    if (!path) return;
    const normalizedPath = path.replace(/\\/g, "/");
    const originalFileName = normalizedPath.split("/").pop();
    if (!originalFileName) return;

    const res = await authFetch(
      `${config.apiBaseUrl}/api/changes/downloadFile?path=${encodeURIComponent(
        path,
      )}`,
    );
    if (!res.ok) throw new Error("Failed to download file");

    const blob = await res.blob();
    const url = window.URL.createObjectURL(blob);

    const a = document.createElement("a");
    a.href = url;
    a.download = originalFileName;
    document.body.appendChild(a);
    a.click();
    a.remove();
    window.URL.revokeObjectURL(url);
  } catch (err) {
    console.error("Download error:", err);
  }
};

const Modal: React.FC<ModalProps> = ({ isOpen, data, onClose }) => {
  const navigate = useNavigate();
  const [selectedChange, setSelectedChange] = useState<ChangeData | null>(null);
  const [localChangeData, setLocalChangeData] = useState<any>(data);
  const isAdmin = localStorage.getItem("isAdmin") === "true";
  const [isForwardModalOpen, setIsForwardModalOpen] = useState(false);
  const [journalMessages, setJournalMessages] = useState<Message[]>([]);
  const [communicationMessages, setCommunicationMessages] = useState<
    Communication[]
  >([]);
  const [relatedTickets, setRelatedTickets] = useState<any[]>([]);

  const [showConfirmClose, setShowConfirmClose] = useState(false);
  const [showChangePhase, setShowChangePhase] = useState(false);
  const [journalTitle, setJournalTitle] = useState("Add Journal");

  const [ShowJournal, setShowJournal] = useState(false);
  const [ShowCommunication, setShowCommunication] = useState(false);
  const [fileImages, setFileImages] = useState<{
    [path: string]: string | null;
  }>({});
  const tableHeaderStyle = {
    padding: "8px",
    textAlign: "left" as const,
    borderBottom: "2px solid #ccc",
  };

  const tableCellStyle = {
    padding: "8px",
    textAlign: "left" as const,
  };
  const fetchRelatedTickets = async () => {
    try {
      if (!data?.relatedobject) return;

      const ticketIds = Array.isArray(data.relatedobject)
        ? data.relatedobject
        : data.relatedobject.split(",").map((t: string) => t.trim());

      if (ticketIds.length === 0) return;

      const res = await authFetch(`${config.apiBaseUrl}/api/incidents/search`, {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify(ticketIds),
      });

      if (!res.ok) throw new Error("Failed to fetch related tickets");

      const result = await res.json();
      setRelatedTickets(result);
    } catch (error) {
      console.error("Error fetching related tickets:", error);
    }
  };
  // New flag to distinguish Add Journal from Pause
  const [isPauseAction, setIsPauseAction] = useState(false);
  const renderSection = (title: string, keys: string[], data: any) => (
    <div className="modal-section">
      <h3>{title}</h3>
      <div className="modal-section-content">
        {keys
          .filter((key) => {
            if (
              !isAdmin &&
              [
                "reactionPoint",
                "responseTimeToOpen",
                "responseTimeToClose",
                "impact",
                "priority",
                "acceptanceTime",
                "sla",
                "slaStatus",
                "ola",
                "olaStatus",
                "rmaCrossReference",
                "relatedobject",
                "customerComplaint",
                "customerInformed",
              ].includes(key)
            ) {
              return false;
            }
            return data[key] !== undefined && !hiddenKeys.includes(key);
          })
          .map((key, index) => (
            <div key={index} className="modal-item">
              <strong>{fieldLabels[key] || key}:</strong>
              <div className="modal-value">
                {key === "filePath" ? (
                  /* ================= FILE PATH LOGIC (UNCHANGED) ================= */
                  Array.isArray(data[key]) ? (
                    <ul style={{ listStyle: "none", padding: 0, margin: 0 }}>
                      {data[key].map((path: string | null, i: number) => {
                        if (!path) return null;

                        const normalizedPath = path.replace(/\\/g, "/");
                        const fileName =
                          normalizedPath.split("/").pop() || `File${i + 1}`;
                        const ext = fileName.split(".").pop()?.toLowerCase();
                        const isImage = [
                          "png",
                          "jpg",
                          "jpeg",
                          "gif",
                          "bmp",
                          "webp",
                        ].includes(ext || "");
                        const imgUrl = fileImages[path] || null;

                        return (
                          <li key={i} style={{ marginBottom: "10px" }}>
                            {isImage && imgUrl ? (
                              <img
                                src={imgUrl}
                                alt={fileName}
                                style={{
                                  maxWidth: "100%",
                                  maxHeight: "300px",
                                  borderRadius: "5px",
                                }}
                              />
                            ) : (
                              <button
                                style={{
                                  color: "blue",
                                  textDecoration: "underline",
                                  background: "none",
                                  border: "none",
                                  cursor: "pointer",
                                }}
                                onClick={() => downloadFile(path)}
                              >
                                {fileName}
                              </button>
                            )}
                          </li>
                        );
                      })}
                    </ul>
                  ) : (
                    (() => {
                      const normalizedPath =
                        data[key]?.replace(/\\/g, "/") || "";
                      const fileName =
                        normalizedPath.split("/").pop() || "file";
                      const ext = fileName.split(".").pop()?.toLowerCase();
                      const isImage = [
                        "png",
                        "jpg",
                        "jpeg",
                        "gif",
                        "bmp",
                        "webp",
                      ].includes(ext || "");
                      const fileUrl = `${config.apiBaseUrl}/api/changes/files/${fileName}`;

                      return isImage ? (
                        <img
                          src={fileUrl}
                          alt={fileName}
                          style={{
                            maxWidth: "100%",
                            maxHeight: "300px",
                            borderRadius: "5px",
                          }}
                        />
                      ) : (
                        <button
                          style={{
                            color: "blue",
                            textDecoration: "underline",
                            background: "none",
                            border: "none",
                            cursor: "pointer",
                          }}
                          onClick={() => downloadFile(data[key])}
                        >
                          {fileName}
                        </button>
                      );
                    })()
                  )
                ) : key === "userid" ? (
                  username
                ) : key === "relatedobject" && data[key] ? (
                  /* ================= ✅ RELATED OBJECT LOGIC (ADDED) ================= */
                  (Array.isArray(data[key])
                    ? data[key]
                    : (data[key] as string).split(",")
                  ).map((item: string, idx: number) => {
                    const ticketId = item.trim();
                    let path = `/incident-edit/${ticketId}`;

                    if (ticketId.startsWith("TSK"))
                      path = `/task-edit/${ticketId}`;
                    else if (ticketId.startsWith("CHN"))
                      path = `/change-edit/${ticketId}`;
                    else if (ticketId.startsWith("IND"))
                      path = `/incident-edit/${ticketId}`;

                    const total = Array.isArray(data[key])
                      ? data[key].length
                      : (data[key] as string).split(",").length;

                    return (
                      <React.Fragment key={idx}>
                        <button
                          style={{
                            color: "blue",
                            textDecoration: "underline",
                            background: "none",
                            border: "none",
                            cursor: "pointer",
                            padding: 0,
                            marginRight: 5,
                          }}
                          onClick={() => navigate(path, { state: { data } })}
                        >
                          {ticketId}
                        </button>
                        {idx < total - 1 ? ", " : ""}
                      </React.Fragment>
                    );
                  })
                ) : (
                  /* ================= DEFAULT VALUE RENDERING ================= */
                  (() => {
                    const value = data[key];

                    if (
                      value === "" ||
                      JSON.stringify(value) === "[]" ||
                      JSON.stringify(value) === "{}"
                    )
                      return "";

                    if (
                      key === "description" ||
                      key === "summary" ||
                      key === "risk" ||
                      key === "rollbackstrategy" ||
                      key === "requiredresources" ||
                      key === "solution"
                    ) {
                      return (
                        <div
                          dangerouslySetInnerHTML={{ __html: value }}
                          style={{ whiteSpace: "normal" }}
                        />
                      );
                    }

                    if (Array.isArray(value)) return value.join(", ");

                    if (typeof value === "string") {
                      if (/^\d{4}-\d{2}-\d{2}T\d{2}:\d{2}:\d{2}/.test(value))
                        return formatDate(value);
                      return value;
                    }

                    return JSON.stringify(value, null, 2);
                  })()
                )}
              </div>
            </div>
          ))}
      </div>
    </div>
  );
  const [username, setUsername] = useState<string>("");
  useEffect(() => {
    if (isOpen) {
      //console.log("dataaaaaaaaaaaaaa", data);
      setLocalChangeData(data);
      fetchMessages();
      fetchUsername();
      fetchCommunication();
      fetchRelatedTickets();
    }
  }, [isOpen, data]);
  const fetchUsername = async () => {
    if (!data?.userid) return;
    try {
      const res = await authFetch(
        `${config.apiBaseUrl}/api/users/${data.userid}`,
      );
      if (!res.ok) throw new Error("User not found");
      const user = await res.json();
      setUsername(user.username || `User #${data.userid}`);
    } catch (err) {
      setUsername(`User #${data.userid}`);
    }
  };
  const closeForwardModal = () => {
    setIsForwardModalOpen(false);
    setSelectedChange(null);
  };

  const fetchMessages = async () => {
    if (!data?.ticketId) return;
    const res = await authFetch(
      `${config.apiBaseUrl}/api/messages/ticket/${data.ticketId}`,
    );
    if (res.ok) setJournalMessages(await res.json());
  };

  const fetchCommunication = async () => {
    if (!data?.ticketId) return;
    const res = await authFetch(
      `${config.apiBaseUrl}/api/communications/ticket/${data.ticketId}`,
    );
    if (res.ok) setCommunicationMessages(await res.json());
  };

  if (!isOpen) return null;

  const fetchUpdateChanges = async (): Promise<ChangeData> => {
    const res = await authFetch(
      `${config.apiBaseUrl}/api/changes/${data.ticketId}`,
    );
    return await res.json();
  };
  const isAccepted = data?.isaccepted;
  const isClosed = data?.isclosed;
  const isPaused = data?.ispaused;
  const handleForward = (data: ChangeData) => {
    setSelectedChange(data);
    setIsForwardModalOpen(true);
  };
  const confirmClose = async () => {
    setShowConfirmClose(false);
    onClose();
    await new Promise((resolve) => setTimeout(resolve, 4000));
    const updatedData = await fetchUpdateChanges();
    await handleClose(updatedData, fetchMessages);
  };

  const addcommunication = () => {
    setShowCommunication(false);
    onClose();
  };

  return (
    <>
      {isForwardModalOpen && (
        <ForwardModal
          isOpen={isForwardModalOpen}
          data={selectedChange}
          onClose={closeForwardModal}
        />
      )}

      {showConfirmClose && (
        <ConfirmCloseModal
          data={data}
          onConfirm={confirmClose}
          onCancel={() => setShowConfirmClose(false)}
        />
      )}
      {showChangePhase && (
        <ChangePhaseModel
          data={data}
          onConfirm={confirmClose}
          onCancel={() => setShowChangePhase(false)}
        />
      )}
      {ShowJournal && (
        <JournalModal
          data={data}
          title={journalTitle}
          onConfirm={async (journalText: string) => {
                       // await authFetch(`${config.apiBaseUrl}/api/messages`, {
            //   method: "POST",
            //   headers: { "Content-Type": "application/json" },
            //   body: JSON.stringify({
            //     ticketId: data.ticketId,
            //     message: journalText,
            //     username:username
            //   }),
            // });

            if (isPauseAction) {
              await handlePause(data, fetchMessages);
              setJournalTitle("Pause Change (Add Journal)");
            }

            setShowJournal(false);
            fetchMessages();
            setIsPauseAction(false);
          }}
          onCancel={() => {
            setShowJournal(false);
            setIsPauseAction(false);
          }}
        />
      )}

      {ShowCommunication && (
        <CommunicationModal
          data={data}
          onConfirm={addcommunication}
          onCancel={() => setShowCommunication(false)}
        />
      )}

      <div className="modal-overlay">
        <div className="modal-content">
          <div
            className="modal-top-buttons"
            style={{
              position: "sticky",
              top: -32,
              backgroundColor: "#fff",
              zIndex: 100,
              padding: "10px",
              display: "flex",
              flexWrap: "wrap",
              gap: "10px",
            }}
          >
            {" "}
            {isAdmin && (
              <button
                onClick={() =>
                  navigate("/task-add", { state: { changeData: data } })
                }
              >
                Create Task
              </button>
            )}{" "}
            {!isClosed && isAdmin && (
              <button onClick={() => setShowChangePhase(true)}>
                Change Phase
              </button>
            )}
            {!isClosed && isAdmin && (
              <button onClick={() => navigate(`/change-edit/${data.ticketId}`)}>
                Edit
              </button>
            )}
            {!isAccepted && isAdmin && (
              <button onClick={() => handleAccept(data, fetchMessages)}>
                Accept
              </button>
            )}
            {isAccepted && !isPaused && !isClosed && isAdmin && (
              <>
                <button disabled>Already Accepted</button>
                {/* Pause button sets isPauseAction to true before showing modal */}
                <button
                  onClick={() => {
                    setIsPauseAction(true);
                    setShowJournal(true);
                  }}
                >
                  Pause
                </button>
                <button onClick={() => setShowConfirmClose(true)}>Close</button>
              </>
            )}
            {isPaused && isAdmin && (
              <>
                <button onClick={() => handleReactivate(data, fetchMessages)}>
                  Reactivate
                </button>
                <button onClick={() => setShowConfirmClose(true)}>Close</button>
              </>
            )}
            {isClosed && isAdmin && (
              <button onClick={() => handleReopen(data, fetchMessages)}>
                Reopen
              </button>
            )}
            {isAdmin && !isClosed && (
              <button onClick={() => handleForward(data)}>Forward</button>
            )}
            <button onClick={onClose}>X</button>
          </div>

          <h2
            style={{
              position: "sticky",
              top: 16, // slightly below the buttons
              backgroundColor: "#fff",
              zIndex: 99,
              margin: 0,
              padding: "10px 0",
            }}
          >
            Change Details
          </h2>
          <div className="modal-body">
            {renderSection("Change Information", ticketInfo, data)}
            {renderSection("Customer & Network", customerInfo, data)}
            {renderSection("Change Severity", incInfo, data)}
            {renderSection("Change & Details", statusInfo, data)}
            {isAdmin && renderSection("Type of Change", template, data)}
            {isAdmin && renderSection("Responsibility", resInfo, data)}
            {renderSection("Attachments", relInfo, data)}
            {relatedTickets.length > 0 && (
              <div className="modal-section">
                <h3>Related Tickets</h3>

                <div style={{ overflowX: "auto" }}>
                  <table
                    style={{
                      width: "100%",
                      borderCollapse: "collapse",
                      marginTop: "10px",
                    }}
                  >
                    <thead>
                      <tr style={{ background: "#f4f4f4" }}>
                        <th style={tableHeaderStyle}>Ticket ID</th>
                        <th style={tableHeaderStyle}>Responsible Person</th>
                        <th style={tableHeaderStyle}>Closure Code</th>
                        <th style={tableHeaderStyle}>Type</th>
                        <th style={tableHeaderStyle}>Status</th>
                      </tr>
                    </thead>

                    <tbody>
                      {relatedTickets.map((ticket, index) => {
                        const ticketId = ticket.ticketNumber;
                        if (!ticketId) return null;

                        let path = "/incident-edit/" + ticketId;

                        if (ticketId.startsWith("TSK"))
                          path = "/task-edit/" + ticketId;
                        else if (ticketId.startsWith("CHN"))
                          path = "/change-edit/" + ticketId;

                        return (
                          <tr
                            key={index}
                            style={{ borderBottom: "1px solid #ddd" }}
                          >
                            {/* ✅ Ticket ID as hyperlink */}
                            <td style={tableCellStyle}>
                              <span
                                onClick={() =>
                                  navigate(path, { state: { data: ticket } })
                                }
                                style={{
                                  color: "blue",
                                  textDecoration: "underline",
                                  cursor: "pointer",
                                }}
                              >
                                {ticketId}
                              </span>
                            </td>

                            <td style={tableCellStyle}>
                              {ticket["Responsible Person"] || "-"}
                            </td>

                            <td style={tableCellStyle}>
                              {ticket.closure || "-"}
                            </td>

                            <td style={tableCellStyle}>{ticket.type || "-"}</td>

                            <td style={tableCellStyle}>
                              {ticket.status || "-"}
                            </td>
                          </tr>
                        );
                      })}
                    </tbody>
                  </table>
                </div>
              </div>
            )}
          </div>

          <div className="messages-container">
            {isAdmin && (
              <div className="journal">
                {isAdmin && (
                  <button
                    onClick={() => {
                      setIsPauseAction(false);
                      setShowJournal(true);
                      setJournalTitle("Add Journal");
                    }}
                  >
                    Add Journal
                  </button>
                )}
                <h3>Journal</h3>
                <ul style={{ listStyle: "none", padding: 0 }}>
                  {[...journalMessages]
                    .sort(
                      (a, b) =>
                        new Date(b.date).getTime() - new Date(a.date).getTime(),
                    )
                    .map((msg) => (
                      <li
                        key={msg.id}
                        style={{
                          marginBottom: "1rem",
                          wordBreak: "break-word",
                        }}
                      >
                        <strong>{msg.username}</strong>:&nbsp;
                        <span
                          dangerouslySetInnerHTML={{ __html: msg.message }}
                          style={{
                            display: "inline-block",
                            maxWidth: "100%",
                            wordWrap: "break-word",
                            overflowWrap: "break-word",
                          }}
                        />
                        <br />
                        <small>{new Date(msg.date).toLocaleString()}</small>
                      </li>
                    ))}
                </ul>
              </div>
            )}
            <div className="journal">
              <button onClick={() => setShowCommunication(true)}>
                Add Communication
              </button>

              <h3>Communication</h3>
              <ul>
                {[...communicationMessages]
                  .sort(
                    (a, b) =>
                      new Date(b.date).getTime() - new Date(a.date).getTime(),
                  )
                  .map((msg) => (
                    <li key={msg.id}>
                      <strong>{msg.users}</strong>: {msg.message}
                      <br />
                      <small>{new Date(msg.date).toLocaleString()}</small>
                    </li>
                  ))}
              </ul>
            </div>
          </div>
        </div>
      </div>
    </>
  );
};

export default Modal;
